import { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, LinearProgress, Alert, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { PageFlip } from 'page-flip/dist/js/page-flip.module.js';
import { useTranslation } from '../../../language';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Pages are rendered to images at this width — sharp enough for a two-page
// spread on large screens without holding huge bitmaps in memory
const RENDER_WIDTH = 1100;

// ---------------------------------------------------------------------------
// ReportBooklet — renders a PDF page by page (pdfjs) and shows it as a
// page-flip booklet. `loadUrl` returns a fresh signed URL (they expire).
// ---------------------------------------------------------------------------
export default function ReportBooklet({ loadUrl }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const hostRef = useRef(null);
  const flipRef = useRef(null);
  const [pages, setPages] = useState(null);      // { urls, width, height }
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);      // signed URL of the failed PDF, for the fallback link
  const [failed, setFailed] = useState(false);
  const [current, setCurrent] = useState(0);

  // ── 1. Render every PDF page to an image ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let urls = [];
    let task = null;
    const run = async () => {
      let url = null;
      try {
        url = await loadUrl();
        if (cancelled || !url) throw new Error('no url');
        task = getDocument({ url });
        const pdf = await task.promise;
        if (cancelled) return;
        setProgress({ done: 0, total: pdf.numPages });
        let size = null;
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
          if (!size) size = { width: base.width, height: base.height };
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          await page.render({ canvas, viewport }).promise;
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
          urls.push(URL.createObjectURL(blob));
          if (cancelled) return;
          setProgress({ done: n, total: pdf.numPages });
        }
        setPages({ urls, ...size });
      } catch {
        if (!cancelled) { setError(url); setFailed(true); }
      }
    };
    run();
    return () => {
      cancelled = true;
      task?.destroy();
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [loadUrl]);

  // ── 2. Mount the page-flip book once the images are ready ─────────────────
  useEffect(() => {
    if (!pages || !hostRef.current) return;
    // page-flip removes its root element on destroy, so it gets its own node
    // instead of one React manages
    const el = document.createElement('div');
    hostRef.current.appendChild(el);
    const pf = new PageFlip(el, {
      width: pages.width,
      height: pages.height,
      size: 'stretch',
      minWidth: 200,
      maxWidth: 700,
      minHeight: 280,
      maxHeight: Math.round(window.innerHeight * 0.68),
      showCover: true,
      maxShadowOpacity: 0.4,
      flippingTime: reduceMotion ? 1 : 700,
      mobileScrollSupport: false,
    });
    pf.loadFromImages(pages.urls);
    pf.on('flip', (e) => setCurrent(e.data));
    flipRef.current = pf;
    setCurrent(0);
    return () => { flipRef.current = null; pf.destroy(); };
  }, [pages, reduceMotion]);

  // ── 3. Arrow keys turn the pages ──────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') flipRef.current?.flipNext();
      if (e.key === 'ArrowLeft') flipRef.current?.flipPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (failed) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        {t('hc_report_error')}{' '}
        {error && (
          <a href={error} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>
            {t('hc_report_open_tab')}
          </a>
        )}
      </Alert>
    );
  }

  if (!pages) {
    return (
      <Box sx={{ py: 6, px: { xs: 1, sm: 6 }, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {progress.total
            ? t('hc_report_loading_pages').replace('{n}', progress.done).replace('{total}', progress.total)
            : t('hc_report_loading')}
        </Typography>
        <LinearProgress
          variant={progress.total ? 'determinate' : 'indeterminate'}
          value={progress.total ? (progress.done / progress.total) * 100 : 0}
          sx={{ width: '100%', maxWidth: 320, borderRadius: 2 }}
        />
      </Box>
    );
  }

  const total = pages.urls.length;
  const navSx = {
    color: theme.palette.text.primary,
    border: '1px solid', borderColor: 'divider',
    '&.Mui-disabled': { opacity: 0.35 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box ref={hostRef} sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => flipRef.current?.flipPrev()} disabled={current === 0} aria-label={t('hc_report_prev')} sx={navSx}>
          <ChevronLeft size={20} strokeWidth={2.5} />
        </IconButton>
        <Typography variant="body2" aria-live="polite" sx={{ minWidth: 72, textAlign: 'center', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {current + 1} / {total}
        </Typography>
        <IconButton onClick={() => flipRef.current?.flipNext()} disabled={current >= total - 1} aria-label={t('hc_report_next')} sx={navSx}>
          <ChevronRight size={20} strokeWidth={2.5} />
        </IconButton>
      </Box>
    </Box>
  );
}
